import {
	createIsRecommendedItem,
	type IsRecommendedItem,
} from '@atlaskit/editor-common/quick-insert/is-recommended-item';
import {
	ADD_SUGGESTIONS_TO_CONTENT_SKILL_MENU_ITEM,
	HYPERLINK_MENU_ITEM,
	MENTION_MENU_ITEM,
} from '@atlaskit/editor-common/quick-insert/keys';

import { useStableEventCallback } from '../../common/hooks/useStableEventCallback';

type PromptSurface = 'chat' | 'inline';

type QuickInsertRecommendationOptions = {
	isRecommendedItem: IsRecommendedItem;
	maxRecommendedItems: 5;
};

const skillMenuItemKey = (slug: string) => `skill:${slug}`;

const noRecommendations: QuickInsertRecommendationOptions = {
	isRecommendedItem: () => null,
	maxRecommendedItems: 5,
};

const createRecommendations = (itemKeys: string[]): QuickInsertRecommendationOptions => ({
	isRecommendedItem: createIsRecommendedItem(itemKeys),
	maxRecommendedItems: 5,
});

const mentionAndLink = [MENTION_MENU_ITEM.key, HYPERLINK_MENU_ITEM.key];

const recommendationsByProductAndSurface: Record<
	'confluence' | 'jira',
	Record<PromptSurface, QuickInsertRecommendationOptions>
> = {
	confluence: {
		inline: createRecommendations([
			ADD_SUGGESTIONS_TO_CONTENT_SKILL_MENU_ITEM.key,
			skillMenuItemKey('create-action-plan'),
			...mentionAndLink,
		]),
		chat: createRecommendations([
			ADD_SUGGESTIONS_TO_CONTENT_SKILL_MENU_ITEM.key,
			skillMenuItemKey('daily-briefing'),
			...mentionAndLink,
		]),
	},
	jira: {
		inline: createRecommendations(mentionAndLink),
		chat: createRecommendations([
			skillMenuItemKey('create-work-items'),
			skillMenuItemKey('update-work-items'),
			skillMenuItemKey('build-skill'),
			...mentionAndLink,
		]),
	},
};

const getRovoPromptQuickInsertRecommendations = (
	productKey: string,
	surface: PromptSurface,
	isSpecialistPromptInput: boolean,
	isStagingArea: boolean,
): QuickInsertRecommendationOptions => {
	if (isSpecialistPromptInput || isStagingArea) {
		return noRecommendations;
	}

	if (productKey === 'confluence' || productKey === 'jira') {
		return recommendationsByProductAndSurface[productKey][surface];
	}

	return noRecommendations;
};

export const useRovoPromptQuickInsertRecommendations = (
	productKey: string,
	surface: PromptSurface,
	isSpecialistPromptInput: boolean,
	isStagingArea: boolean,
): QuickInsertRecommendationOptions => {
	const { isRecommendedItem, maxRecommendedItems } = getRovoPromptQuickInsertRecommendations(
		productKey,
		surface,
		isSpecialistPromptInput,
		isStagingArea,
	);

	return {
		isRecommendedItem: useStableEventCallback(isRecommendedItem),
		maxRecommendedItems,
	};
};
